import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, AlertTriangle, Download, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MoneyTabs from "@/components/coach/MoneyTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

/**
 * Tax Liability dashboard — show what was collected from customers, grouped
 * by month + quarter, so the owner knows exactly what to remit to WA DOR.
 * Read-only. Zero impact on charging.
 */
export default function TaxLiability() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== "owner") {
            toast.error("Owner only");
            navigate("/coach", { replace: true });
        }
    }, [authLoading, user, navigate]);

    useEffect(() => {
        if (user?.role !== "owner") return;
        setLoading(true);
        axios
            .get(`${API}/coach/accounting/tax-liability?year=${year}`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch((e) => toast.error("Couldn't load", { description: e?.response?.data?.detail }))
            .finally(() => setLoading(false));
    }, [user, year]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            ["Month", "Tax collected ($)", "Gross sales ($)", "# charges"],
            ...data.months.map((m) => [
                m.month,
                (m.tax_cents / 100).toFixed(2),
                (m.gross_cents / 100).toFixed(2),
                m.count,
            ]),
            ["YTD", (data.ytd.tax_cents / 100).toFixed(2), (data.ytd.gross_cents / 100).toFixed(2), data.ytd.count],
        ];
        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `maxsc-tax-liability-${data.year}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Exported CSV");
    };

    if (loading || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white pb-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
                <MoneyTabs />
                <button
                    type="button"
                    onClick={() => navigate("/coach")}
                    className="text-zinc-400 hover:text-white text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1.5 mb-6"
                    data-testid="tax-liability-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to console
                </button>

                <div className="flex items-end justify-between mb-2 gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">
                            Owner controls
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl tracking-wider uppercase flex items-center gap-3">
                            <Receipt className="w-7 h-7 text-brand-red" /> Tax Liability
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value))}
                            className="bg-black border border-zinc-700 px-3 py-1.5 text-sm"
                            data-testid="tax-year-select"
                        >
                            {[0, 1, 2].map((offset) => {
                                const y = new Date().getFullYear() - offset;
                                return <option key={y} value={y}>{y}</option>;
                            })}
                        </select>
                        <button
                            type="button"
                            onClick={exportCsv}
                            className="btn-tactical text-xs"
                            data-testid="tax-export-csv"
                        >
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                    </div>
                </div>

                {/* YTD hero */}
                <div className="mt-6 border border-amber-700/40 bg-amber-950/10 p-5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 mb-1">
                                YTD Tax Owed to WA DOR
                            </div>
                            <div className="font-display text-4xl text-white" data-testid="tax-ytd-amount">
                                {fmtMoney(data.ytd.tax_cents)}
                            </div>
                            <div className="text-xs text-amber-200/80 mt-2 max-w-prose leading-relaxed">
                                {data.note}
                            </div>
                            <div className="text-[11px] text-zinc-400 mt-2">
                                Gross sales: <span className="font-mono text-white">{fmtMoney(data.ytd.gross_cents)}</span> ·{" "}
                                {data.ytd.count} chargeable events
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quarterly */}
                <div className="mt-6">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        By quarter
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {data.quarters.map((q) => (
                            <div
                                key={q.quarter}
                                data-testid={`tax-quarter-${q.quarter}`}
                                className="border border-zinc-800 px-4 py-3"
                            >
                                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                                    {q.label}
                                </div>
                                <div className="text-2xl font-display text-white mt-1">
                                    {fmtMoney(q.tax_cents)}
                                </div>
                                <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                                    gross {fmtMoney(q.gross_cents)} · {q.count}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Monthly table */}
                <div className="mt-8">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        Monthly breakdown
                    </div>
                    <div className="border border-zinc-800">
                        <table className="w-full text-sm" data-testid="tax-monthly-table">
                            <thead className="bg-zinc-950 border-b border-zinc-800">
                                <tr>
                                    <th className="text-left text-[10px] tracking-[0.2em] uppercase text-zinc-400 px-3 py-2">Month</th>
                                    <th className="text-right text-[10px] tracking-[0.2em] uppercase text-zinc-400 px-3 py-2">Tax collected</th>
                                    <th className="text-right text-[10px] tracking-[0.2em] uppercase text-zinc-400 px-3 py-2">Gross sales</th>
                                    <th className="text-right text-[10px] tracking-[0.2em] uppercase text-zinc-400 px-3 py-2">Events</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.months.map((m) => (
                                    <tr
                                        key={m.month}
                                        data-testid={`tax-month-${m.month}`}
                                        className={`border-b border-zinc-900 ${m.tax_cents > 0 ? "" : "opacity-40"}`}
                                    >
                                        <td className="px-3 py-2 text-zinc-300">{m.month_name} {data.year}</td>
                                        <td className="px-3 py-2 text-right font-mono text-white">{fmtMoney(m.tax_cents)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-zinc-400">{fmtMoney(m.gross_cents)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-zinc-500">{m.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="border-t border-zinc-700 bg-zinc-950">
                                <tr>
                                    <td className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-zinc-300">YTD</td>
                                    <td className="px-3 py-2 text-right font-mono text-white">{fmtMoney(data.ytd.tax_cents)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-white">{fmtMoney(data.ytd.gross_cents)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-white">{data.ytd.count}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <p className="text-[10px] text-zinc-500 mt-4 leading-relaxed max-w-prose">
                    File your WA DOR returns at{" "}
                    <a href="https://dor.wa.gov" target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white underline">
                        dor.wa.gov
                    </a>
                    . Use this report to fill in the &ldquo;Retail Sales Tax&rdquo; line on your return.
                </p>
            </div>
        </div>
    );
}
